"use client";

import { type FormEvent, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { PUBLIC_API_URL } from "@/lib/api";

const BRAND = "#0d1737";

const inputClassName =
  "mt-1 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-offset-1 focus:outline-none";

export default function ForgotPasswordView() {
  const t = useTranslations("ForgotPasswordPage");
  const locale = useLocale();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${PUBLIC_API_URL}/api/auth/forgot-password/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail ?? t("errorGeneric"));
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-8 shadow-lg">
        <div className="text-center">
          <div className="mx-auto flex w-full justify-center">
            <Image
              src="/construxia-logo.png"
              alt="Construxia"
              width={2286}
              height={294}
              priority
              unoptimized
              className="h-12 w-auto"
            />
          </div>
          <p className="mt-3 text-sm text-slate-500">{t("subtitle")}</p>
        </div>

        {done ? (
          <div className="mt-8 space-y-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              {t("success")}
            </div>
            <Link
              href={`/${locale}/login`}
              className="block text-center text-sm font-medium"
              style={{ color: BRAND }}
            >
              {t("backToLogin")}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                {t("email")}
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className={inputClassName}
                style={{
                  boxShadow: "none",
                  outline: "none",
                  ["--tw-ring-color" as never]: BRAND,
                }}
              />
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: BRAND }}
            >
              {submitting ? t("submitting") : t("submit")}
            </button>

            <Link
              href={`/${locale}/login`}
              className="block text-center text-sm text-slate-500 hover:text-slate-700"
            >
              {t("backToLogin")}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
